<template>
  <div class="veritas-offer-doc">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">
          {{ schoolName }}
          <span class="text-primary">| Offer Letter</span>
        </h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2">
          {{ reportItems.length }} Letters
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading offer letters...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="documents-wrap">
      <div
        v-for="(staff, index) in reportItems"
        :key="staff.id || index"
        class="letter-set"
      >
        <article class="letter-page letter-page--single">
          <header class="letterhead">
            <img
              :src="letterheadUrl"
              :alt="schoolName"
              class="letterhead__logo"
              @error="onLetterheadError"
            />
            <div class="letterhead__text">
              <p class="letterhead__school">{{ schoolName }}</p>
              <p v-if="campusName" class="letterhead__campus">{{ campusName }}</p>
              <p v-if="displayCampusAddress" class="letterhead__address">
                {{ displayCampusAddress }}
              </p>
            </div>
          </header>

          <div class="letter-body">
            <div class="meta-row">
              <span class="meta-ref">
                Ref No.
                <span class="meta-em">{{ referenceNumber(staff) }}</span>
              </span>
              <span class="meta-date">
                Date:
                <span class="meta-em">{{ currentDateLabel }}</span>
              </span>
          </div>

            <div class="addressee">
              <p class="recipient-name">{{ staffDisplayName(staff) }},</p>
              <p v-if="staffAddressLine(staff)" class="recipient-address">
                ({{ staffAddressLine(staff) }})
              </p>
            </div>

            <p class="subject-line">
              Ref:
              <span class="subject-em">
                Offer Letter for the Position of
                {{ offerPositionLine(staff) }}
              </span>
            </p>

            <p class="salutation">Dear {{ staffDisplayName(staff) }},</p>

            <p class="body-text">
              This is in continuation to your application for the position
              captioned in subject line and subsequently this refers your
              interviews with our honorable Directors, we are pleased to
              inform you that management has finally selected you for this
              position on the terms &amp; conditions discussed with you in
              your last interview conducted on
              {{ interviewDateLabel(staff) }}.
            </p>

            <p class="body-text">
              We appreciate your interest in our Institute; please submit the
              acceptance to HR within the seven days of issuance of this
              letter enabling us to complete the appointment procedure.
            </p>

            <p class="body-text">
              Please feel free to contact undersigned for any further
              information regarding your job descriptions.
            </p>

            <p class="closing">Best Regards,</p>
            </div>

          <div class="signature-row">
            <div class="sign-col sign-col--director">
              <div class="sign-gap"></div>
              <div class="sign-line"></div>
              <p class="sign-label">Director</p>
            </div>
            <div class="sign-col sign-col--acceptance">
              <div class="acceptance-panel">
                <p class="acceptance-title">Acceptance</p>
                <div class="sign-gap sign-gap--sm"></div>
                <div class="sign-line"></div>
                <p class="acceptance-label">Signature</p>
                <p class="acceptance-detail">{{ staffDisplayName(staff) }}</p>
                <p class="acceptance-detail">
                  CNIC: {{ staffCnic(staff) || "—" }}
                </p>
                <p class="acceptance-detail">
                  Cell: {{ staffCell(staff) || "—" }}
                </p>
                <p class="acceptance-detail acceptance-detail--date">
                  Date:
                  <span class="date-rule" aria-hidden="true"></span>
                </p>
            </div>
            </div>
          </div>
        </article>
        </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import {
  parseStaffIds,
  parseKentDepIds,
  loadKentCampusInfo,
  loadKentStaffRecords,
  loadKentStaffByDepartments,
  staffDisplayName,
  staffDesignation,
  staffAddress,
  staffField,
  isGeneralStaffLetterDomain,
  VERITAS_LETTERHEAD_IMAGE,
  usesCampusLogoLetterhead,
  generalStaffLetterRefPrefix,
} from "./kentStaffPrintUtils";
import { getMediaPreviewUrl, handleDrivePreviewError } from "@/utils/gdriveUpload";

function formatVeritasOfferDate(value) {
  if (value === null || value === undefined || value === "") return "—";
  let date;
  if (value instanceof Date) {
    date = value;
  } else {
    const serial =
      typeof value === "number"
        ? value
        : typeof value === "string" && /^\d{4,5}$/.test(String(value).trim())
        ? Number(String(value).trim())
        : NaN;
    if (!Number.isNaN(serial) && serial > 30000 && serial < 60000) {
      const utcDays = Math.floor(serial - 25569);
      date = new Date(utcDays * 86400 * 1000);
    } else {
      date = new Date(value);
    }
  }
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  const month = date.toLocaleDateString("en-GB", { month: "long" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export default {
  name: "VeritasTeachersFormPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      schoolName: "Veritas Schooling System",
      campusName: "",
      campusAddress: "",
      db: "",
      cID: 0,
      staffIDs: [],
      depIDs: [],
      staffStatus: "present",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      letterheadUrl: VERITAS_LETTERHEAD_IMAGE,
      letterheadFallback: "/Myskool-icon-01.svg",
      campusSchoolLogo: "",
    };
  },
  computed: {
    currentDateLabel() {
      return formatVeritasOfferDate(new Date());
    },
    displayCampusAddress() {
      return (this.campusAddress || "").trim();
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    staffDisplayName,
    staffDesignation,
    initReport() {
      if (!isGeneralStaffLetterDomain(this.reportDomain)) {
        this.error = "This report is not available for this school.";
        return;
      }

      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0);
      this.staffIDs = parseStaffIds(query);
      this.depIDs = parseKentDepIds(query);
      this.staffStatus = query.status || "present";

      if (!this.db) {
        this.error = "Database identifier is missing.";
        return;
      }
      if (!this.staffIDs.length && !this.depIDs.length) {
        this.error = "No staff or department selected for offer letters.";
        return;
      }

      this.loadReportData();
    },
    staffAddressLine(staff) {
      const addr = staffAddress(staff);
      return addr && addr !== "—" ? addr : "";
    },
    staffCnic(staff) {
      const v = staffField(staff, ["cnic", "CNIC", "nic"], "");
      return v && v !== "—" ? v : "";
    },
    staffCell(staff) {
      const v = staffField(
        staff,
        ["contact_number", "mobile", "phone", "contact", "cell"],
        ""
      );
      return v && v !== "—" ? v : "";
    },
    referenceNumber(staff) {
      const fromStaff = staffField(
        staff,
        [
          "refNo",
          "refNumber",
          "referenceNo",
          "offerRef",
          "letterRef",
          "appointmentRef",
        ],
        ""
      );
      if (fromStaff && fromStaff !== "—") return fromStaff;
      const id = staff?.id;
      const prefix = generalStaffLetterRefPrefix(this.reportDomain);
      return id ? `${prefix}/${id}` : `${prefix}/—`;
    },
    offerPositionLine(staff) {
      const designation = staffDesignation(staff);
      const campus = (this.campusName || "").trim();
      if (campus && designation && designation !== "—") {
        const campusLower = campus.toLowerCase();
        const desLower = designation.toLowerCase();
        if (campusLower.includes(desLower) || desLower.includes(campusLower)) {
          return designation;
        }
        if (/campus/i.test(campus)) {
          return `${designation} ${campus}`;
        }
        return `${designation} ${campus} Campus`;
      }
      return designation || campus || "—";
    },
    interviewDateRaw(staff) {
      const fromField = staffField(
        staff,
        [
          "lastInterviewDate",
          "interviewDate",
          "interviewOn",
          "lastInterviewOn",
        ],
        null
      );
      if (fromField && fromField !== "—") return fromField;
      return (
        staff?.interviews?.date ||
        staff?.lastInterview?.date ||
        staff?.appointedOn ||
        staff?.appointmentDate ||
        staff?.joiningDate ||
        null
      );
    },
    interviewDateLabel(staff) {
      const raw = this.interviewDateRaw(staff);
      if (raw === null || raw === "" || raw === "—") return "—";
      return formatVeritasOfferDate(raw);
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const campus = await loadKentCampusInfo(
          this,
          this.db,
          this.cID,
          this.reportDomain
        );
        this.schoolName = campus.schoolName;
        this.campusName = campus.campusName;
        this.campusAddress = campus.campusAddress;
        this.campusSchoolLogo = campus.schoolLogo || "";
        this.setLetterheadUrl(this.campusSchoolLogo);

        if (this.staffIDs.length) {
          this.reportItems = await loadKentStaffRecords(
            this,
            this.db,
            this.cID,
            this.staffIDs
          );
        } else {
          this.reportItems = await loadKentStaffByDepartments(
            this,
            this.db,
            this.cID,
            this.depIDs,
            this.staffStatus
          );
        }

        if (!this.reportItems.length) {
          this.error = "No staff records found for the selected staff.";
        }
      } catch (err) {
        console.error("Veritas offer letter error:", err);
        this.error = "Failed to load staff records for offer letters.";
      } finally {
        this.loading = false;
      }
    },
    setLetterheadUrl(schoolLogo) {
      if (usesCampusLogoLetterhead(this.reportDomain)) {
        if (!schoolLogo) {
          this.letterheadUrl = this.letterheadFallback;
          return;
        }
        const raw = String(schoolLogo);
        if (raw.startsWith("blob:") || /^https?:\/\//i.test(raw)) {
          this.letterheadUrl = raw;
          return;
        }
        this.letterheadUrl = getMediaPreviewUrl(schoolLogo, "Image");
        return;
      }
      this.letterheadUrl = VERITAS_LETTERHEAD_IMAGE;
    },
    onLetterheadError(event) {
      if (
        usesCampusLogoLetterhead(this.reportDomain) &&
        this.campusSchoolLogo &&
        !String(this.campusSchoolLogo).startsWith("blob:")
      ) {
        handleDrivePreviewError(event, this.campusSchoolLogo, "image");
        return;
      }
      if (event.target.src !== this.letterheadFallback) {
        event.target.src = this.letterheadFallback;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;600;700&display=swap");

$accent: #0c2340;
$accent-soft: #1a4a7a;
$gold: #b8860b;
$ink: #111827;
$muted: #4b5563;
$font-main: "Source Serif 4", Georgia, "Times New Roman", serif;

.veritas-offer-doc {
  background: #e8ecf2;
  min-height: 100vh;
  padding: 2rem;
  font-family: $font-main;
  color: $ink;
}

.control-bar {
  background: #fff;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 10;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  cursor: pointer;
  font-family: "Segoe UI", sans-serif;
}

.btn-print {
  background: linear-gradient(135deg, $accent 0%, $accent-soft 100%);
  color: #fff;
}

.btn-close {
  background: #fff;
  border: 1px solid #dfe6e9;
  color: #636e72;
}

.documents-wrap {
  margin: 0 auto;
}

.letter-set {
  margin-bottom: 2rem;
}

.letter-page {
  width: 210mm;
  padding: 18mm 18mm 16mm;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #fff;
  font-size: 13pt;
  line-height: 1.85;
  color: $ink;

  p {
    margin-bottom: 14pt;
  }

  &--single {
    height: 296mm;
    min-height: 296mm;
    max-height: 296mm;
    overflow: hidden;
  }
}

@media screen {
  .letter-page {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    margin: 20px auto;
    border-radius: 2px;
  }
}

.letterhead {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 5mm;
  flex-shrink: 0;
  padding-bottom: 0.35rem;
  border-bottom: 2px solid $accent;

  &__logo {
    flex-shrink: 0;
    width: 22mm;
    max-width: 22mm;
  height: auto;
    max-height: 20mm;
  object-fit: contain;
    object-position: left center;
  }

  &__text {
    flex: 1;
    min-width: 0;
    text-align: center;
  }

  &__school {
  margin: 0;
    font-size: 13pt;
  font-weight: 700;
    letter-spacing: 0.04em;
  text-transform: uppercase;
    color: $accent;
  line-height: 1.25;
}

  &__campus {
  margin: 0.2rem 0 0;
    font-size: 10.5pt;
    font-weight: 700;
    color: $accent-soft;
    line-height: 1.3;
  }

  &__address {
  margin: 0.12rem 0 0;
    font-size: 9.5pt;
    font-weight: 600;
    color: $muted;
    line-height: 1.35;
  }
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  margin-bottom: 1.35rem;
  font-size: 11pt;
  font-weight: 600;
}

.meta-em,
.subject-em {
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.addressee {
  margin-bottom: 1.2rem;
}

.recipient-name {
  margin: 0 0 0.15rem;
  font-size: 11.5pt;
    font-weight: 700;
  color: #000;
}

.recipient-address {
  margin: 0;
  font-size: 11pt;
  font-weight: 600;
  color: $muted;
}

.subject-line {
  margin: 0 0 1.25rem;
  padding: 0.55rem 0.7rem;
  font-size: 11pt;
  font-weight: 600;
  line-height: 1.65;
  background: #f8fafc;
  border-left: 3px solid $gold;
  color: #000;
}

.salutation {
  margin: 0 0 1.1rem;
  font-size: 12pt;
  font-weight: 400;
  line-height: 1.65;
}

.letter-body {
  flex: 1 1 auto;
  min-height: 0;
}

.body-text {
  margin: 0 0 1.25rem;
  text-align: justify;
  font-size: 11.5pt;
  font-weight: 600;
  line-height: 1.9;
  color: #1f2937;

  &:last-child {
    margin-bottom: 0;
  }
}

.closing {
  margin: 14pt 0 0;
  font-size: 12pt;
  font-weight: 400;
}

.signature-row {
  margin-top: auto !important;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2.25rem;
  padding-top: 0.35rem;
}

.sign-col--director {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  flex: 0 0 auto;
  width: 14rem;
  min-width: 14rem;
  max-width: 14rem;
  padding-top: 0.25rem;
  text-align: center;

  .sign-line {
    width: 100%;
    min-width: 14rem;
  }

  .sign-label {
    width: 100%;
    text-align: center;
  }
}

.sign-col--acceptance {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  margin-left: auto;
  max-width: none;
}

.acceptance-panel {
  width: 72mm;
  max-width: 72mm;
  box-sizing: border-box;
  padding: 0.4rem 0.55rem 0.38rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fafbfc;

  .sign-gap--sm {
    height: 1.65rem;
    min-height: 1.65rem;
  }

  .acceptance-title {
    margin: 0;
    font-size: 11pt;
    line-height: 1.25;
  }

  .acceptance-label {
    margin: 0.28rem 0 0;
    font-size: 11pt;
    line-height: 1.25;
  }

  .acceptance-detail {
    margin: 0.14rem 0 0;
    font-size: 10.5pt;
    line-height: 1.3;

    &--date {
      margin-top: 0.28rem;
    }
  }

  .date-rule {
    height: 1em;
  }
}

.sign-gap {
  height: 3.5rem;
  min-height: 3.5rem;

  &--sm {
    height: 2.75rem;
    min-height: 2.75rem;
  }
}

.sign-line {
  border-top: 1.5px solid #000;
  width: 100%;
}

.sign-label,
.acceptance-title,
.acceptance-label {
  margin: 0.35rem 0 0;
  font-size: 12pt;
    font-weight: 700;
}

.acceptance-title {
  margin-top: 0;
  margin-bottom: 0;
}

.acceptance-detail {
  margin: 0.2rem 0 0;
  font-size: 12pt;
  font-weight: 400;
  line-height: 1.35;

  &--date {
    margin-top: 0.35rem;
    display: flex;
    align-items: baseline;
    gap: 0.35rem;
  }
}

.date-rule {
  flex: 1 1 auto;
  min-width: 3.5rem;
  max-width: 5.5rem;
  border-bottom: 1px solid #000;
  height: 1.1em;
  display: inline-block;
}

@media print {
  .no-print {
    display: none !important;
  }

  .veritas-offer-doc {
    background: #fff;
    padding: 0;
  }

  .documents-wrap {
    max-width: none;
    margin: 0;
  }

  .letter-set {
    margin: 0;
  }

  .letter-set + .letter-set {
    page-break-before: always;
    break-before: page;
  }

  .documents-wrap,
  .letter-set {
    width: 210mm !important;
    max-width: 210mm !important;
    min-width: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }

  .veritas-offer-doc {
    width: 210mm !important;
    max-width: 210mm !important;
    overflow: hidden !important;
  }

  .letter-page {
    box-shadow: none !important;
    margin: 0 !important;
    transform: none !important;
    zoom: 1 !important;
    display: flex !important;
    flex-direction: column !important;
    box-sizing: border-box !important;
  }

  .letter-page--single {
    width: 210mm !important;
    height: 296mm !important;
    min-height: 296mm !important;
    max-height: 296mm !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .letter-page--single .letter-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    display: block !important;
  }

  .letter-page--single .signature-row {
    margin-top: auto !important;
  }

  .letter-page--single .signature-row {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .letter-set:last-child .letter-page--single {
    page-break-after: auto !important;
    break-after: auto !important;
  }

  .letter-set:not(:last-child) .letter-page--single {
    page-break-after: always !important;
    break-after: page !important;
  }

  .letterhead {
    margin-bottom: 4mm !important;

    &__logo {
      width: 22mm !important;
      max-width: 22mm !important;
      max-height: 18mm !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    &__school,
    &__campus,
    &__address {
      color: #000 !important;
    }
  }

  .subject-line {
    background: #f3f4f6 !important;
    border-left: 3pt solid #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .acceptance-panel {
    width: 72mm !important;
    max-width: 72mm !important;
    padding: 0.38rem 0.5rem 0.35rem !important;

    .sign-gap--sm {
      height: 1.6rem !important;
      min-height: 1.6rem !important;
    }
    border: 1.5pt solid #000 !important;
    background: #fff !important;
    border-radius: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .body-text,
  .meta-row,
  .recipient-name,
  .subject-line,
  .salutation,
  .closing,
  .acceptance-detail {
    color: #000 !important;
  }
}
</style>

<style lang="scss">
@import "./veritasLetterPrint.global.scss";
</style>
